import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { LayerGroup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useMapStyle, type ResolvedMapStyle } from '@/components/map/mapColors';

/**
 * Vector-tile renderer for the live parcel layer.
 *
 * Why: the JSON+SVG path topped out near 500 polygons. Server-rendered
 * MVT tiles fetched on demand let us render every live parcel (3,500+
 * today, scales to 100k+) without sacrificing UX. Same library
 * (`protomaps-leaflet`) and lifecycle pattern as the ICAS PMTiles
 * layers — the difference is the source: instead of a static
 * `.pmtiles` file, we point at `/api/parcels/tiles/{z}/{x}/{y}`
 * served by Fastify + geojson-vt + vt-pbf.
 *
 * Live data is reflected via a `?v=<version>` query parameter:
 * `/api/parcels/tiles/version` returns the current version stamp;
 * every parcel mutation bumps it server-side. Once we know it, we
 * burn it into the tile URL so the browser HTTP cache invalidates
 * cleanly on the next change.
 *
 * Pane: shared `parcelTilePane` z-index 450 — above the default
 * `overlayPane` (400, where photo polygons might land) and below
 * `markerPane` (600) so photo markers stay clickable on top.
 */

const PARCEL_PANE = 'parcelTilePane';

function ensureParcelPane(map: L.Map): HTMLElement {
  const existing = map.getPane(PARCEL_PANE);
  if (existing) return existing;
  const pane = map.createPane(PARCEL_PANE);
  pane.style.zIndex = '450';
  return pane;
}

export type ParcelSummary = {
  id: string;
  ucf: string | null;
  codProiect: string | null;
  raion: string | null;
  primarie: string | null;
  anul: number | null;
};

/** Subset of `GET /api/parcels/nearest` response we care about for
 *  click → panel resolution. `contains: true` = click is inside this
 *  polygon. `contains: false` = nearest match by centroid (distanceKm
 *  carries how far). We open the panel for both, gated by a max
 *  fallback radius — see CLICK_FALLBACK_RADIUS_KM below. */
type NearestResp = {
  id: string;
  contains: boolean;
  distanceKm?: number;
  codProiect: string | null;
  raion: string;
  primarie: string;
  ucf: string;
};

/**
 * When the click LANDS OUTSIDE every PNERP parcel polygon, the panel
 * still opens for the closest parcel within this radius. Anything
 * further away is treated as "clicked far from any forestry data" and
 * the click becomes a no-op (same behaviour as before this fallback).
 *
 * 0.5 km is the rough size of an ICAS forestry-entity boundary on
 * screen at z14 — large enough that clicking the green ICAS overlay
 * still surfaces the nearest project parcel, small enough that a
 * stray click on OSM background doesn't pop a panel for a parcel a
 * city away.
 */
const CLICK_FALLBACK_RADIUS_KM = 0.5;

type PmLayer = L.Layer & {
  remove: () => void;
};

type LeafletPmModule = {
  leafletLayer: (opts: Record<string, unknown>) => PmLayer;
  PolygonSymbolizer?: new (opts: Record<string, unknown>) => unknown;
  LineSymbolizer?: new (opts: Record<string, unknown>) => unknown;
  CenteredTextSymbolizer?: new (opts: Record<string, unknown>) => unknown;
};

const moduleCache: { promise: Promise<LeafletPmModule> | null } = { promise: null };
function loadProtomaps(): Promise<LeafletPmModule> {
  if (moduleCache.promise) return moduleCache.promise;
  moduleCache.promise = import('protomaps-leaflet') as unknown as Promise<LeafletPmModule>;
  return moduleCache.promise;
}

/**
 * Build paint rules. Year-based per-feature coloring was attempted via
 * function-valued `fill`, but protomaps-leaflet 4.0.x's PolygonSymbolizer
 * silently swallows arity-2 callbacks in some code paths, so polygons
 * rendered transparent. Using a constant green fill that's visible at
 * every zoom for now — per-year colors can come back as a follow-up
 * once we wire `StringAttr` properly.
 */
function buildPaintRules(
  mod: LeafletPmModule,
  filterRef: { current: ((props: Record<string, unknown>) => boolean) | undefined },
  style: ResolvedMapStyle,
): unknown[] {
  const PolySym = mod.PolygonSymbolizer;
  const LineSym = mod.LineSymbolizer;

  // Production paint: vivid forest-green with a darker outline at all
  // zoom levels. Without the outline at country zoom, individual
  // parcels (1-2 px wide there) disappear into OSM's natural green
  // forest cover. The stroke is what makes them recognizable as
  // distinct polygons. Geometry simplification at low zoom keeps the
  // paint cheap even with stroke on.
  // The fill stays light so the stand condition on satellite imagery
  // shows through; the stroke carries the boundary instead.
  const { colors } = style;
  const fillOpts = { fill: colors.parcelFill, opacity: style.parcelFillOpacity };
  const fillSym = PolySym ? new PolySym(fillOpts) : fillOpts;
  const strokeOpts = { color: colors.parcelLine, width: 1.5 };
  const strokeSym = LineSym ? new LineSym(strokeOpts) : strokeOpts;
  const haloOpts = style.parcelHalo ? { color: style.parcelHalo, width: 3.5 } : null;
  const haloSym = haloOpts ? (LineSym ? new LineSym(haloOpts) : haloOpts) : null;

  // protomaps-leaflet rule signature: `filter(zoom, feature)` returns
  // boolean. Closing over `filterRef` means a new predicate takes
  // effect on the next paint without rebuilding rules or remounting
  // the layer.
  const ruleFilter = (
    _z: number,
    f: { props?: Record<string, unknown> } | undefined,
  ) => {
    const pred = filterRef.current;
    if (!pred) return true;
    return pred(f?.props ?? {});
  };

  return [
    {
      dataLayer: 'parcels',
      symbolizer: fillSym,
      minzoom: 0,
      maxzoom: 24,
      filter: ruleFilter,
    },
    ...(haloSym
      ? [{ dataLayer: 'parcels', symbolizer: haloSym, minzoom: 0, maxzoom: 24, filter: ruleFilter }]
      : []),
    {
      dataLayer: 'parcels',
      symbolizer: strokeSym,
      minzoom: 0,
      maxzoom: 24,
      filter: ruleFilter,
    },
  ];
}

/**
 * Label rules — render the UA / UCF code as a centered label per
 * polygon. Kicks in at z9 because that's exactly the zoom range the
 * cluster bubble flyTo lands on (CLUSTER_THRESHOLD_ZOOM); users
 * coming from a bubble click must see the codes immediately to know
 * which polygon to tap for photos. The `label` property is baked
 * into every MVT feature server-side (parcel-tiles.ts) as
 * `r.ua ?? r.ucf ?? ''`.
 */
function buildLabelRules(
  mod: LeafletPmModule,
  filterRef: { current: ((props: Record<string, unknown>) => boolean) | undefined },
): unknown[] {
  const Centered = mod.CenteredTextSymbolizer;
  if (!Centered) return [];
  // Smaller font at low zoom so 1000+ labels don't dominate the
  // viewport; larger at street zoom where users actually read them.
  const labelSymSmall = new Centered({
    labelProps: ['label'],
    fill: '#0f172a',
    stroke: '#ffffff',
    width: 3,
    fontFamily: 'sans-serif',
    fontSize: 10,
    fontWeight: 600,
  });
  const labelSymLarge = new Centered({
    labelProps: ['label'],
    fill: '#0f172a',
    stroke: '#ffffff',
    width: 3,
    fontFamily: 'sans-serif',
    fontSize: 13,
    fontWeight: 600,
  });
  // Same filter pattern as the paint rules — hide labels for
  // features the predicate rejects so the screen doesn't get cluttered
  // with codes of polygons that aren't visible.
  const ruleFilter = (
    _z: number,
    f: { props?: Record<string, unknown> } | undefined,
  ) => {
    const pred = filterRef.current;
    if (!pred) return true;
    return pred(f?.props ?? {});
  };
  return [
    {
      dataLayer: 'parcels',
      symbolizer: labelSymSmall,
      minzoom: 9,
      maxzoom: 12,
      filter: ruleFilter,
    },
    {
      dataLayer: 'parcels',
      symbolizer: labelSymLarge,
      minzoom: 12,
      maxzoom: 24,
      filter: ruleFilter,
    },
  ];
}

type Props = {
  enabled: boolean;
  /** Below this zoom level the layer is hidden (the cluster bubbles
   *  take over). Default 0 means visible at every zoom. /app/filter
   *  passes 9 so individual parcels only render once the user zooms
   *  in to a useful scale; reduces canvas overdraw at country view
   *  where polygons would be sub-pixel anyway. */
  minZoom?: number;
  /**
   * Per-feature visibility predicate, called once per polygon per
   * paint. Returning `false` skips both the fill and the stroke for
   * that feature — used by /app/filter to reflect filter narrowing
   * on the map without re-fetching tiles (the MVT bytes still
   * carry every parcel, but the canvas only paints matches).
   *
   * Note: protomaps-leaflet evaluates rule filters per tile-render,
   * so this is cheap. The closure reads from `filterRef.current` so
   * the parent can update the predicate without remounting the
   * tile layer.
   */
  filterPredicate?: (props: Record<string, unknown>) => boolean;
  onSelect?: (parcel: ParcelSummary) => void;
};

/**
 * Fetch the current parcel tile version from the API. Used to burn a
 * cache-busting query param onto the tile URL so the browser cache
 * naturally invalidates when parcels change.
 */
async function fetchTileVersion(): Promise<number> {
  try {
    const resp = await fetch('/api/parcels/tiles/version', {
      credentials: 'include',
    });
    if (!resp.ok) return 0;
    const json = (await resp.json()) as { version?: number };
    return typeof json.version === 'number' ? json.version : 0;
  } catch {
    return 0;
  }
}

export function ParcelVectorTilesLayer({
  enabled,
  minZoom = 0,
  filterPredicate,
  onSelect,
}: Props) {
  const map = useMap();
  const style = useMapStyle();
  const groupRef = useRef<L.LayerGroup>(null);
  const [version, setVersion] = useState<number | null>(null);
  const [zoom, setZoom] = useState<number>(() => map.getZoom());

  useEffect(() => {
    const onZoom = () => setZoom(map.getZoom());
    map.on('zoomend', onZoom);
    return () => {
      map.off('zoomend', onZoom);
    };
  }, [map]);

  const zoomActive = zoom >= minZoom;

  // Pin `onSelect` to a ref so the protomaps init effect below doesn't
  // tear down + recreate the tile layer on every parent re-render.
  // Without this, every filter change in FilterPage would re-mount the
  // protomaps layer before it could finish fetching tiles, and the user
  // would see no polygons at all (this was the real bug, not the URL
  // or the paint rules).
  const onSelectRef = useRef(onSelect);
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  // Same ref pattern for the filter predicate so paint rules read the
  // CURRENT predicate at draw time without us tearing down + rebuilding
  // the protomaps layer on every filter keystroke. When the predicate
  // changes we nudge BOTH the paint cache and the labeler cache:
  //
  //   clearLayout()    drops every label previously placed by the
  //                    labeler subsystem; without this the UA/UCF
  //                    text stays painted for polygons the new
  //                    predicate just hid.
  //   rerenderTiles()  forces tiles through the paint rules again so
  //                    the polygon fill/stroke updates.
  //
  // The two together are what makes the layer truly track the filter
  // state. redraw() is the Leaflet GridLayer fallback for older
  // protomaps-leaflet builds that don't expose rerenderTiles().
  const filterRef = useRef(filterPredicate);
  const pmLayerRef = useRef<PmLayer | null>(null);
  useEffect(() => {
    filterRef.current = filterPredicate;
    const layer = pmLayerRef.current as unknown as
      | {
          rerenderTiles?: () => void;
          redraw?: () => void;
          clearLayout?: () => void;
        }
      | null;
    if (!layer) return;
    if (typeof layer.clearLayout === 'function') layer.clearLayout();
    if (typeof layer.rerenderTiles === 'function') layer.rerenderTiles();
    else if (typeof layer.redraw === 'function') layer.redraw();
  }, [filterPredicate]);

  useLayoutEffect(() => {
    if (map) ensureParcelPane(map);
  }, [map]);

  // Resolve the cache-busting version stamp once per mount.
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    fetchTileVersion().then((v) => {
      if (!cancelled) setVersion(v);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !zoomActive || version === null) return;
    let mounted = true;
    let pmLayer: PmLayer | null = null;
    let clickHandler: ((e: L.LeafletMouseEvent) => void) | null = null;

    ensureParcelPane(map);

    // Relative tile URL — Vite's `/api` proxy maps to localhost:4000 in
    // dev; Vercel rewrites it to api.pnerp.org (or wherever) in prod.
    // protomaps-leaflet's ZxySource accepts protocol-relative or
    // root-relative URLs and resolves them against the page origin.
    const tileUrl = `/api/parcels/tiles/{z}/{x}/{y}.pbf?v=${version}`;

    loadProtomaps()
      .then((mod) => {
        if (!mounted) return;
        const paintRules = buildPaintRules(mod, filterRef, style);
        const labelRules = buildLabelRules(mod, filterRef);
        pmLayer = mod.leafletLayer({
          url: tileUrl,
          paint_rules: paintRules,
          paintRules,
          // protomaps-leaflet 4 expects `label_rules`; pass camelCase
          // too for v3 compatibility, same trick as paint_rules.
          label_rules: labelRules,
          labelRules,
          maxDataZoom: 18,
          attribution: '',
          pane: PARCEL_PANE,
        });

        const group = groupRef.current;
        if (group) {
          group.addLayer(pmLayer);
        } else {
          pmLayer.addTo(map);
        }
        // Stash the layer so the `filterPredicate` effect can call
        // rerenderTiles() on changes without rebuilding the layer.
        pmLayerRef.current = pmLayer;

        // Click resolution: server-side point-in-polygon via
        // `GET /api/parcels/nearest?lat&lon`. We tried the
        // protomaps-leaflet `queryFeatures(lng, lat, brushSize)` path
        // first — it was unreliable for plain XYZ tile sources (the
        // picked Map's keys vary, and at low zoom features get
        // simplified out of the hit-test geometry). The server-side
        // PiP is authoritative, ~50 ms, and uses the same database
        // rows the tile builder reads — so a click on a visible
        // polygon always resolves to that polygon.
        clickHandler = (e: L.LeafletMouseEvent) => {
          const cb = onSelectRef.current;
          if (!cb) return;
          const lat = e.latlng.lat;
          const lon = e.latlng.lng;
          fetch(`/api/parcels/nearest?lat=${lat}&lon=${lon}`, {
            credentials: 'include',
          })
            .then((r) => (r.ok ? r.json() : null))
            .then((json: { nearest?: NearestResp } | null) => {
              const n = json?.nearest;
              if (!n) return;
              // Containment is always good; otherwise accept the
              // nearest match if it's inside the fallback radius.
              // This lets users click the visible ICAS reference
              // polygons (the green forestry boundaries that aren't
              // PNERP parcels but live in our overlays) and still
              // get the panel for the project parcel closest to
              // their click — matches the "polygon should be
              // clickable" expectation for any forestry area.
              if (
                !n.contains &&
                (typeof n.distanceKm !== 'number' ||
                  n.distanceKm > CLICK_FALLBACK_RADIUS_KM)
              ) {
                return; // click was on empty map / OSM background
              }
              cb({
                id: n.id,
                ucf: n.ucf ?? null,
                codProiect: n.codProiect ?? null,
                raion: n.raion ?? null,
                primarie: n.primarie ?? null,
                anul: null,
              });
            })
            .catch(() => {
              /* network blip — click is a no-op */
            });
        };
        map.on('click', clickHandler);
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.warn('[parcels] vector tile layer disabled:', err);
      });

    return () => {
      mounted = false;
      if (clickHandler) map.off('click', clickHandler);
      if (pmLayer) {
        const group = groupRef.current;
        if (group) group.removeLayer(pmLayer);
        else pmLayer.remove();
      }
      pmLayerRef.current = null;
    };
    // Intentionally NOT depending on `onSelect` or `filterPredicate` —
    // both are accessed via refs above so the latest value is used
    // without remounting the tile layer. A colour change rebuilds it, which
    // is rare enough not to matter.
  }, [enabled, zoomActive, version, map, style]);

  if (!enabled || !zoomActive) return null;
  return <LayerGroup ref={groupRef} />;
}
